import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Image, Link, Text
} from 'components/ui';
import HistorySectionCard from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistorySectionCard';
import HistorySectionStatus from 'components/Content/BeautyInsider/BeautyInsiderHistory/HistorySectionStatus';
import PointsActivityCard from 'components/Content/BeautyInsider/BeautyInsiderModules/PointsActivityCard/PointsActivityCard';
import InfoButton from 'components/InfoButton/InfoButton';
import beautyInsiderPageBindings from 'analytics/bindingMethods/pages/beautyInsider/beautyInsiderPageBindings';
import { HISTORY_SECTION_STATUS } from 'components/Content/BeautyInsider/BeautyInsiderHistory/historyConstants';
import localeUtils from 'utils/LanguageLocale';
import { space } from 'style/config';
import HistoryTabEN from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_en_US';
import HistoryTabFR from 'components/Content/BeautyInsider/BeautyInsiderHistory/locales/HistoryTab_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(HistoryTabEN, HistoryTabFR);
const PLACEHOLDER_SECTION_MIN_HEIGHT = space[9] - space[1];
const EMPTY_SECTION_MIN_HEIGHT = space[9] + space[8] + space[2];
const ENTER = 'Enter';
const SPACE = ' ';

/**
 * Renders the Points History section with grouped point activity expansion.
 *
 * Supports loading, error, placeholder, empty, and populated statuses. Populated sections show the first three point
 * groups, fire expand analytics when a group opens, and expose optional info, View all, and disclaimer content.
 *
 * @param {Object} props
 * @param {Object} props.section Points History presentation data with groups, infoAction, viewAll, and disclaimer.
 * @returns {React.ReactElement} Points History card and optional disclaimer.
 */
const HistoryPointsSection = ({ section }) => {
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const isEmpty = section.status === HISTORY_SECTION_STATUS.EMPTY;
    const isPlaceholder = section.status === HISTORY_SECTION_STATUS.PLACEHOLDER;
    const sectionMinHeight = isPlaceholder ? PLACEHOLDER_SECTION_MIN_HEIGHT : isEmpty ? EMPTY_SECTION_MIN_HEIGHT : undefined;

    const handleToggle = record => {
        const isExpanding = expandedGroupId !== record.groupId;

        setExpandedGroupId(isExpanding ? record.groupId : null);

        if (isExpanding) {
            beautyInsiderPageBindings.firePointsActivityExpandAnalytics(record);
        }
    };

    const handleInfoKeyDown = event => {
        if (event.key === ENTER || event.key === SPACE) {
            event.preventDefault();
            section.infoAction?.action?.(event);
        }
    };

    const renderHeaderAction = () => {
        if (!section.viewAll) {
            return null;
        }

        return (
            <Link
                color='blue'
                href={section.viewAll.href}
                onClick={section.viewAll.action}
                children={section.viewAll.label}
            />
        );
    };

    const renderInfoButton = () => (
        <InfoButton
            marginLeft={2}
            aria-label={section.infoAction?.label}
            onClick={section.infoAction?.action}
            onKeyDown={handleInfoKeyDown}
        />
    );

    const renderContent = () => {
        if (
            section.status === HISTORY_SECTION_STATUS.LOADING ||
            section.status === HISTORY_SECTION_STATUS.ERROR ||
            section.status === HISTORY_SECTION_STATUS.PLACEHOLDER
        ) {
            return (
                <HistorySectionStatus
                    loadingLabel={getText('loadingPointsHistory')}
                    retryLabel={getText('retry')}
                    section={section}
                />
            );
        }

        if (section.status === HISTORY_SECTION_STATUS.EMPTY) {
            return (
                <Box css={styles.emptyContent}>
                    {section.emptyState?.illustration && (
                        <Image
                            display='block'
                            size={48}
                            src={section.emptyState.illustration}
                        />
                    )}
                    <Text
                        is='p'
                        lineHeight='tight'
                        children={section.emptyState?.body}
                    />
                </Box>
            );
        }

        return (
            <Box marginTop={2}>
                {section.groups.slice(0, 3).map((record, index) => (
                    <PointsActivityCard
                        key={record.groupId || index}
                        record={record}
                        onToggle={() => handleToggle(record)}
                        isExpanded={expandedGroupId === record.groupId}
                    />
                ))}
            </Box>
        );
    };

    return (
        <Box>
            <HistorySectionCard
                section={section}
                dataTestId='history-points-section'
                headerAction={renderHeaderAction()}
                headerMarginBottom={4}
                minHeight={sectionMinHeight}
                titleAddon={renderInfoButton()}
            >
                {renderContent()}
            </HistorySectionCard>
            {section.disclaimer && (
                <Text
                    marginTop={2}
                    is='p'
                    color='gray'
                    fontSize='sm'
                    children={section.disclaimer}
                />
            )}
        </Box>
    );
};

HistoryPointsSection.propTypes = {
    section: PropTypes.shape({
        title: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
        cardStyle: PropTypes.string,
        groups: PropTypes.arrayOf(PropTypes.object),
        infoAction: PropTypes.object,
        viewAll: PropTypes.object,
        placeholder: PropTypes.object,
        emptyState: PropTypes.object,
        disclaimer: PropTypes.string,
        error: PropTypes.object,
        retry: PropTypes.func
    }).isRequired
};

const styles = {
    emptyContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: space[2],
        alignItems: 'flex-start'
    }
};

export default wrapFunctionalComponent(HistoryPointsSection, 'HistoryPointsSection');
